// src/components/SurveyDetails.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoIosArrowForward } from 'react-icons/io';
import { IoArrowBack, IoCheckmarkCircleOutline, IoAlertCircleOutline } from 'react-icons/io5';
import api from '../services/api';

// --- Small bits ---

const ReadOnlyRow = ({ label, value }) => (
  <div className="grid grid-cols-12 items-center gap-3 mb-3">
    <label className="col-span-12 md:col-span-4 text-sm font-medium text-gray-600">{label}</label>
    <div className="col-span-12 md:col-span-8">
      <div
        className="w-full min-h-[42px] rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
        role="text"
        aria-label={`${label}: ${value || 'N/A'}`}
      >
        {value || 'N/A'}
      </div>
    </div>
  </div>
);

const SubmissionFeedback = ({ status, message, onBack }) => {
  const isSuccess = status === 'success';
  const Icon = isSuccess ? IoCheckmarkCircleOutline : IoAlertCircleOutline;

  return (
    <div className="mx-auto my-10 max-w-3xl px-4">
      <div
        className={`rounded-2xl border p-5 shadow-sm ${
          isSuccess ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`${isSuccess ? 'text-emerald-600' : 'text-rose-600'}`} aria-hidden>
            <Icon size={28} />
          </div>
          <div className="flex-1">
            <h3 className="mb-1 text-base font-semibold">
              {isSuccess ? 'Request processed successfully' : 'Something went wrong'}
            </h3>
            <p className="mb-2 text-sm text-gray-700">{message}</p>
            {!isSuccess && (
              <button
                type="button"
                onClick={onBack}
                className="mt-1 inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              >
                Go back
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main ---

const SurveyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const staticData = location.state?.data || {};
  const currentYear = new Date().getFullYear();

  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const MONTHS = useMemo(
    () => [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    []
  );
  const currentMonth = MONTHS[new Date().getMonth()];

  const [formData, setFormData] = useState({
    researcherInCharge: '',
    projectName: '',
    jobNumber: '',
    year: String(currentYear),
    month: currentMonth, // default to current month
    scriptingInCharge: '',
    dpInCharge: '',
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showInlineError, setShowInlineError] = useState('');

  useEffect(() => {
    document.title = 'Confirm Survey • PMS';
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      researcherInCharge: staticData.researcherInCharge || '',
      projectName: staticData.projectName || '',
      jobNumber: staticData.jobNumber || '',
      year: staticData.year || String(currentYear),
      month: staticData.month || prev.month || currentMonth,
      scriptingInCharge: staticData.scriptingInCharge || '',
      dpInCharge: staticData.dpInCharge || '',
    }));
  }, [staticData, currentYear, currentMonth]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    const pn = (formData.projectName || '').trim();
    const jn = (formData.jobNumber || '').trim();
    const ric = (formData.researcherInCharge || '').trim();
    const y = (formData.year || '').trim();
    const m = (formData.month || '').trim();

    if (!pn) newErrors.projectName = 'Project name is required.';
    if (!jn) newErrors.jobNumber = 'Job number is required.';
    if (!ric) newErrors.researcherInCharge = 'Researcher is required.';
    if (!m) newErrors.month = 'Please select a month.';

    if (!y) {
      newErrors.year = 'Year is required.';
    } else {
      if (!/^\d{4}$/.test(y)) newErrors.year = 'Year must be a 4-digit number.';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const focusFirstError = (errs) => {
    if (errs.month) monthRef.current?.focus();
    else if (errs.year) yearRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowInlineError('');
    const errs = validateForm();
    if (Object.keys(errs).length) {
      focusFirstError(errs);
      return;
    }

    setStatus('submitting');

    const payload = {
      researcher_in_charge: formData.researcherInCharge?.trim(),
      project_name: formData.projectName?.trim(),
      job_number: formData.jobNumber?.trim(),
      year: String(formData.year).trim(),
      month: formData.month?.trim(),
      dp_or_scripting: 'Both',
      scripting_in_charge: formData.scriptingInCharge?.trim(),
      dp_in_charge: formData.dpInCharge?.trim(),
      segment_id: staticData.segmentId || null,
    };

    try {
      const response = await api.post('survey/create-survey/', payload, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });

      // If the API returns success (link sent), go to /projects_list immediately
      setStatus('success');
      setFeedbackMessage(
        response?.data?.message || 'Survey created successfully! A notification email has been sent.'
      );
      navigate('/projects_list', { replace: true });
    } catch (error) {
      console.error('Error while creating survey:', error);
      setStatus('error');
      setFeedbackMessage(
        error?.response?.data?.error || 'An unexpected error occurred. Please try again or contact support.'
      );
      setShowInlineError('We could not create the survey. Check the details and try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (status === 'success' || status === 'error') {
    // Note: on success we navigate immediately; this UI mainly shows for error states
    if (status === 'success') return null;
    return (
      <SubmissionFeedback
        status={status}
        message={feedbackMessage}
        onBack={() => setStatus('idle')}
      />
    );
  }

  return (
    <div className="mx-auto my-10 max-w-3xl px-4">
      {/* Top actions */}
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <IoArrowBack />
          Back
        </button>

        <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
          Step 2 / 2
        </span>
      </div>

      {/* Card */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-100 bg-gradient-to-b from-indigo-50/60 to-white px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="m-0 text-lg font-semibold">Confirm Survey Details</h2>
              <p className="m-0 text-xs text-gray-500">
                Verify the information and complete the required fields.
              </p>
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
            <div className="h-full w-full rounded-full bg-primary transition-[width]" />
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 md:px-10">
          {showInlineError && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
              {showInlineError}
            </div>
          )}

          <form noValidate onSubmit={handleSubmit}>
            <fieldset disabled={status === 'submitting'} className="group">
              <ReadOnlyRow label="Researcher In Charge" value={formData.researcherInCharge} />
              <ReadOnlyRow label="Project Name" value={formData.projectName} />
              <ReadOnlyRow label="Job Number" value={formData.jobNumber} />
              <ReadOnlyRow label="Scripting In-Charge" value={formData.scriptingInCharge} />
              <ReadOnlyRow label="DP In-Charge" value={formData.dpInCharge} />

              {/* Month */}
              <div className="grid grid-cols-12 items-center gap-3 mb-3">
                <label htmlFor="formMonth" className="col-span-12 md:col-span-4 text-sm font-medium text-gray-600">
                  Month <span className="text-rose-500">*</span>
                </label>
                <div className="col-span-12 md:col-span-8">
                  <select
                    id="formMonth"
                    ref={monthRef}
                    aria-label="Select month"
                    value={formData.month}
                    onChange={(e) => setField('month', e.target.value)}
                    className={`w-full rounded-xl border px-3 py-2 text-gray-800 outline-none ring-primary/25 transition focus:ring-4 ${
                      errors.month ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-white focus:border-primary/60'
                    }`}
                    required
                  >
                    <option value="">Select a month…</option>
                    {MONTHS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {errors.month && <p className="mt-1 text-xs text-rose-600">{errors.month}</p>}
                </div>
              </div>

              {/* Year */}
              <div className="grid grid-cols-12 items-center gap-3">
                <label htmlFor="formYear" className="col-span-12 md:col-span-4 text-sm font-medium text-gray-600">
                  Year <span className="text-rose-500">*</span>
                </label>
                <div className="col-span-12 md:col-span-8">
                  <input
                    id="formYear"
                    ref={yearRef}
                    type="number"
                    inputMode="numeric"
                    placeholder={`e.g., ${currentYear}`}
                    value={formData.year}
                    onChange={(e) => {
                      const val = e.target.value.slice(0, 4);
                      if (/^\d{0,4}$/.test(val)) setField('year', val);
                    }}
                    className={`w-full rounded-xl border px-3 py-2 text-gray-800 outline-none ring-primary/25 transition focus:ring-4 ${
                      errors.year ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-white focus:border-primary/60'
                    }`}
                    required
                  />
                  {errors.year && <p className="mt-1 text-xs text-rose-600">{errors.year}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a 4-digit year.
                  </p>
                </div>
              </div>
            </fieldset>

            <div className="mt-6 border-t pt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-[0_6px_14px_rgba(99,102,241,0.25)] transition hover:shadow-[0_8px_20px_rgba(99,102,241,0.35)] disabled:opacity-70"
              >
                {status === 'submitting' ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Processing…
                  </>
                ) : (
                  <>
                    <span>Create Survey &amp; Send Email</span>
                    <IoIosArrowForward aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SurveyDetails;
